import React, { useState, useEffect } from 'react';
import { User } from '../types';
import { ContentSource } from '../types/cms';
import { getUsers, createUser, updateUserRole, updateUserParent, updateUserBrowser, deleteUser, getCurrentUser, updateUserPassword } from '../services/api';
import { contentSourceApi } from '../services/cmsApi';
import ConfirmDialog from '../components/ConfirmDialog';
import { useConfirmDialog } from '../hooks/useConfirmDialog';
import './UserManagement.css';

interface CreateUserData {
    name: string;
    email: string;
    password: string;
    role: 'superadmin' | 'admin' | 'reviewer' | 'editor' | 'editor-reviewer';
    browser?: boolean;
}

const UserManagement: React.FC = () => {
    const [users, setUsers] = useState<User[]>([]);
    const [currentUser, setCurrentUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string>('');
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [createUserData, setCreateUserData] = useState<CreateUserData>({
        name: '',
        email: '',
        password: '',
        role: 'editor',
        browser: false
    });
    const [editingUserId, setEditingUserId] = useState<string | null>(null);
    const [newRole, setNewRole] = useState<string>('');
    const [editingParentId, setEditingParentId] = useState<string | null>(null);
    const [newParentId, setNewParentId] = useState<string>('');
    const [assignedContentSources, setAssignedContentSources] = useState<Record<string, ContentSource[]>>({});
    const { dialogState, showConfirm } = useConfirmDialog();
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [passwordUserId, setPasswordUserId] = useState<string | null>(null);
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [passwordSuccess, setPasswordSuccess] = useState('');

    const roleHierarchy = {
        superadmin: ['superadmin', 'admin', 'reviewer', 'editor', 'editor-reviewer'],
        admin: ['reviewer', 'editor', 'editor-reviewer'],
        reviewer: [],
        editor: []
    };

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        if (users.length > 0) {
            fetchAssignedContentSourcesForUsers(users);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [users]);

    const fetchAssignedContentSourcesForUsers = async (users: User[]) => {
        const results: Record<string, ContentSource[]> = {};
        
        // Initialize all user IDs with empty arrays
        users.forEach(user => {
            results[user.id] = [];
        });
        
        try {
            // Make a single API call to fetch all content sources
            const res = await contentSourceApi.getAll({ limit: 100 });
            const allContentSources = res.data || [];
            
            // Group content sources by assigned_to_id
            allContentSources.forEach((contentSource: ContentSource) => {
                const assignedToId = contentSource.assigned_to_id;
                if (assignedToId && results.hasOwnProperty(assignedToId)) {
                    results[assignedToId].push(contentSource);
                }
            });
        } catch (e) {
            // If API call fails, all user IDs already have empty arrays
            console.error('Failed to fetch content sources:', e);
        }
        
        setAssignedContentSources(results);
    };

    const fetchData = async () => {
        setLoading(true);
        try {
            const [usersData, currentUserData] = await Promise.all([
                getUsers(),
                getCurrentUser()
            ]);
            setUsers(usersData);
            setCurrentUser(currentUserData);
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to fetch data');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateUser = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        try {
            await createUser(createUserData);
            setCreateUserData({ name: '', email: '', password: '', role: 'editor', browser: false });
            setShowCreateForm(false);
            await fetchData();
        } catch (err: any) {
            const errorData = err.response?.data;
            let errorMessage = errorData?.message || 'Failed to create user';
            
            if (errorData?.details) {
                errorMessage += '\n\n' + errorData.details;
            }
            
            if (errorData?.error) {
                errorMessage += '\n\nError type: ' + errorData.error;
            }
            
            setError(errorMessage);
        }
    };

    const handleUpdateRole = async (userId: string) => {
        setError('');

        try {
            await updateUserRole(userId, newRole as any);
            setEditingUserId(null);
            setNewRole('');
            await fetchData();
        } catch (err: any) {
            const errorData = err.response?.data;
            let errorMessage = errorData?.message || 'Failed to update user role';
            
            if (errorData?.details) {
                errorMessage += '\n\n' + errorData.details;
            }
            
            if (errorData?.error) {
                errorMessage += '\n\nError type: ' + errorData.error;
            }
            
            setError(errorMessage);
        }
    };

    const handleUpdateParent = async (userId: string) => {
        setError('');

        try {
            const parentId = newParentId === '' ? null : newParentId;
            await updateUserParent(userId, parentId);
            setEditingParentId(null);
            setNewParentId('');
            await fetchData();
        } catch (err: any) {
            const errorData = err.response?.data;
            let errorMessage = errorData?.message || 'Failed to update user parent';
            
            if (errorData?.details) {
                errorMessage += '\n\n' + errorData.details;
            }
            
            setError(errorMessage);
        }
    };

    const handleUpdateBrowser = async (userId: string, browser: boolean) => {
        setError('');

        try {
            await updateUserBrowser(userId, browser);
            await fetchData();
        } catch (err: any) {
            const errorData = err.response?.data;
            let errorMessage = errorData?.message || 'Failed to update browser access';
            
            if (errorData?.details) {
                errorMessage += '\n\n' + errorData.details;
            }
            
            setError(errorMessage);
        }
    };

    const handleDeleteUser = async (userId: string, userName: string) => {
        const confirmed = await showConfirm({
            title: 'Delete User',
            message: `Are you sure you want to delete user "${userName}"?\n\nThis action cannot be undone.`,
            confirmText: 'Delete',
            cancelText: 'Cancel',
            isDestructive: true
        });

        if (!confirmed) {
            return;
        }

        setError('');

        try {
            await deleteUser(userId);
            await fetchData();
        } catch (err: any) {
            const errorData = err.response?.data;
            let errorMessage = errorData?.message || 'Failed to delete user';
            
            if (errorData?.details) {
                errorMessage += '\n\n' + errorData.details;
            }
            
            if (errorData?.suggestions && errorData.suggestions.length > 0) {
                errorMessage += '\n\nTo resolve this:\n• ' + errorData.suggestions.join('\n• ');
            }
            
            if (errorData?.blockingRelationships) {
                const relationships = errorData.blockingRelationships;
                const relationshipDetails = [];
                
                if (relationships.subordinateUsers > 0) {
                    relationshipDetails.push(`${relationships.subordinateUsers} subordinate user(s)`);
                }
                if (relationships.documents > 0) {
                    relationshipDetails.push(`${relationships.documents} document(s)`);
                }
                if (relationships.books > 0) {
                    relationshipDetails.push(`${relationships.books} book(s)`);
                }
                
                if (relationshipDetails.length > 0) {
                    errorMessage += '\n\nBlocking relationships: ' + relationshipDetails.join(', ');
                }
            }
            
            setError(errorMessage);
        }
    };

    const handleChangePassword = (userId: string) => {
        setPasswordUserId(userId);
        setShowPasswordModal(true);
        setNewPassword('');
        setConfirmPassword('');
        setPasswordError('');
        setPasswordSuccess('');
    };

    const handlePasswordUpdate = async (e: React.FormEvent) => {
        e.preventDefault();
        setPasswordError('');
        setPasswordSuccess('');
        if (newPassword !== confirmPassword) {
            setPasswordError('Passwords do not match');
            return;
        }
        if (!passwordUserId) return;
        try {
            await updateUserPassword(passwordUserId, newPassword);
            setPasswordSuccess('Password updated successfully.');
            setTimeout(() => {
                setShowPasswordModal(false);
            }, 1200);
        } catch (err: any) {
            setPasswordError(err.response?.data?.message || 'Failed to update password');
        }
    };

    const canCreateUser = () => {
        return currentUser?.role === 'superadmin' || currentUser?.role === 'admin';
    };

    const canEditUser = (user: User) => {
        if (!currentUser) return false;
        if (currentUser.role === 'superadmin') return true;
        if (currentUser.role === 'admin') {
            // Admins can edit their direct reports OR any editor/reviewer
            return user.parentId === currentUser.id || 
                   user.role === 'editor' || 
                   user.role === 'reviewer';
        }
        return false;
    };

    const canDeleteUser = (user: User) => {
        if (!currentUser) return false;
        if (user.id === currentUser.id) return false; // Can't delete yourself
        if (currentUser.role === 'superadmin') return true;
        if (currentUser.role === 'admin') {
            // Admins can delete their direct reports OR any editor/reviewer
            return user.parentId === currentUser.id || 
                   user.role === 'editor' || 
                   user.role === 'reviewer';
        }
        return false;
    };

    const canEditParent = (user: User) => {
        // Only superadmin can edit parent relationships
        return currentUser?.role === 'superadmin';
    };

    const getAvailableAdmins = () => {
        // Return all admin users for superadmin to assign as parents
        return users.filter(user => user.role === 'admin');
    };

    const getAvailableRoles = (user?: User) => {
        if (!currentUser) return [];
        
        // If current user is admin and editing an editor/reviewer/editor-reviewer who is not their direct report
        if (currentUser.role === 'admin' && user && 
            (user.role === 'editor' || user.role === 'reviewer' || user.role === 'editor-reviewer') && 
            user.parentId !== currentUser.id) {
            // Only allow switching between editor, reviewer, and editor-reviewer
            return ['editor', 'reviewer', 'editor-reviewer'];
        }
        
        return roleHierarchy[currentUser.role as keyof typeof roleHierarchy] || [];
    };

    const getRoleBadgeClass = (role: string) => {
        switch (role) {
            case 'superadmin': return 'role-badge superadmin';
            case 'admin': return 'role-badge admin';
            case 'reviewer': return 'role-badge reviewer';
            case 'editor': return 'role-badge editor';
            default: return 'role-badge';
        }
    };

    if (loading) {
        return <div className="loading">Loading users...</div>;
    }

    return (
        <div className="user-management">
            <div className="user-management-header">
                <h1>User Management</h1>
                {canCreateUser() && (
                    <button
                        className="btn btn-primary"
                        onClick={() => setShowCreateForm(true)}
                    >
                        Add New User
                    </button>
                )}
            </div>

            {error && (
                <div className="error-message">
                    {error.split('\n').map((line, index) => (
                        <div key={index}>{line}</div>
                    ))}
                </div>
            )}

            {showCreateForm && (
                <div className="modal-overlay">
                    <div className="modal">
                        <div className="modal-header">
                            <h2>Create New User</h2>
                            <button
                                className="modal-close"
                                onClick={() => setShowCreateForm(false)}
                            >
                                ×
                            </button>
                        </div>
                        <form onSubmit={handleCreateUser} className="create-user-form">
                            <div className="form-group">
                                <label htmlFor="name">Name</label>
                                <input
                                    type="text"
                                    id="name"
                                    value={createUserData.name}
                                    onChange={(e) => setCreateUserData({
                                        ...createUserData,
                                        name: e.target.value
                                    })}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="email">Email</label>
                                <input
                                    type="email"
                                    id="email"
                                    value={createUserData.email}
                                    onChange={(e) => setCreateUserData({
                                        ...createUserData,
                                        email: e.target.value
                                    })}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="password">Password</label>
                                <input
                                    type="password"
                                    id="password"
                                    value={createUserData.password}
                                    onChange={(e) => setCreateUserData({
                                        ...createUserData,
                                        password: e.target.value
                                    })}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="role">Role</label>
                                <select
                                    id="role"
                                    value={createUserData.role}
                                    onChange={(e) => setCreateUserData({
                                        ...createUserData,
                                        role: e.target.value as any
                                    })}
                                    required
                                >
                                    {getAvailableRoles().map(role => (
                                        <option key={role} value={role}>
                                            {role.charAt(0).toUpperCase() + role.slice(1)}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-group">
                                <div className="form-checkbox-group">
                                    <label className="form-checkbox-label">
                                        <input
                                            type="checkbox"
                                            checked={createUserData.browser || false}
                                            onChange={(e) => setCreateUserData({
                                                ...createUserData,
                                                browser: e.target.checked
                                            })}
                                            className="form-checkbox-input"
                                        />
                                        <span className="form-checkbox-text">Enable Browser Access</span>
                                    </label>
                                    <p className="form-checkbox-description">
                                        Allow this user to access the application via web browser. If unchecked, user must use the Desktop App.
                                    </p>
                                </div>
                            </div>
                            <div className="form-actions">
                                <button type="button" onClick={() => setShowCreateForm(false)}>
                                    Cancel
                                </button>
                                <button type="submit" className="btn btn-primary">
                                    Create User
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {showPasswordModal && (
                <div className="modal-overlay">
                    <div className="modal">
                        <div className="modal-header">
                            <h2>Change User Password</h2>
                            <button className="modal-close" onClick={() => setShowPasswordModal(false)}>×</button>
                        </div>
                        <form onSubmit={handlePasswordUpdate} className="create-user-form">
                            <div className="form-group">
                                <label htmlFor="new-password">New Password</label>
                                <input
                                    type="password"
                                    id="new-password"
                                    value={newPassword}
                                    onChange={e => setNewPassword(e.target.value)}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="confirm-password">Confirm Password</label>
                                <input
                                    type="password"
                                    id="confirm-password"
                                    value={confirmPassword}
                                    onChange={e => setConfirmPassword(e.target.value)}
                                    required
                                />
                            </div>
                            {passwordError && <div className="error-message">{passwordError}</div>}
                            {passwordSuccess && <div className="success-message" style={{ color: '#27ae60', marginBottom: 12 }}>{passwordSuccess}</div>}
                            <div className="form-actions">
                                <button type="button" onClick={() => setShowPasswordModal(false)}>
                                    Cancel
                                </button>
                                <button type="submit" className="btn btn-primary">
                                    Update Password
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <div className="users-list">
                {users.length === 0 ? (
                    <div className="no-users">No users found.</div>
                ) : (
                    (() => {
                        // Group users by role
                        const usersByRole = users.reduce((acc, user) => {
                            if (!acc[user.role]) {
                                acc[user.role] = [];
                            }
                            acc[user.role].push(user);
                            return acc;
                        }, {} as Record<string, User[]>);

                        // Define role order
                        const roleOrder = ['superadmin', 'admin', 'editor', 'editor-reviewer', 'reviewer'];
                        const availableRoles = roleOrder.filter(role => usersByRole[role] && usersByRole[role].length > 0);

                        const renderUserCard = (user: User) => (
                            <div key={user.id} className="user-card">
                                <div className="user-info">
                                    <h3>{user.name}</h3>
                                    <p className="user-email">{user.email}</p>
                                    <div className="user-role">
                                        {editingUserId === user.id ? (
                                            <div className="role-edit">
                                                <select
                                                    value={newRole}
                                                    onChange={(e) => setNewRole(e.target.value)}
                                                >
                                                    {getAvailableRoles(user).map(role => (
                                                        <option key={role} value={role}>
                                                            {role.charAt(0).toUpperCase() + role.slice(1)}
                                                        </option>
                                                    ))}
                                                </select>
                                                <button
                                                    className="btn btn-sm btn-primary"
                                                    onClick={() => handleUpdateRole(user.id)}
                                                >
                                                    Save
                                                </button>
                                                <button
                                                    className="btn btn-sm btn-secondary"
                                                    onClick={() => {
                                                        setEditingUserId(null);
                                                        setNewRole('');
                                                    }}
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="role-display">
                                                <span className={getRoleBadgeClass(user.role)}>
                                                    {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                                                </span>
                                                {canEditUser(user) && (
                                                    <>
                                                        <button
                                                            className="btn btn-sm btn-secondary"
                                                            onClick={() => {
                                                                setEditingUserId(user.id);
                                                                setNewRole(user.role);
                                                            }}
                                                        >
                                                            Edit Role
                                                        </button>
                                                        <button
                                                            className="btn btn-sm btn-secondary"
                                                            onClick={() => handleChangePassword(user.id)}
                                                        >
                                                            Change Password
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    {editingParentId === user.id ? (
                                        <div className="parent-edit">
                                            <select
                                                value={newParentId}
                                                onChange={(e) => setNewParentId(e.target.value)}
                                            >
                                                <option value="">No Manager (Unassigned)</option>
                                                {getAvailableAdmins().map(admin => (
                                                    <option key={admin.id} value={admin.id}>
                                                        {admin.name} ({admin.email})
                                                    </option>
                                                ))}
                                            </select>
                                            <button
                                                className="btn btn-sm btn-primary"
                                                onClick={() => handleUpdateParent(user.id)}
                                            >
                                                Save
                                            </button>
                                            <button
                                                className="btn btn-sm btn-secondary"
                                                onClick={() => {
                                                    setEditingParentId(null);
                                                    setNewParentId('');
                                                }}
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="user-parent-section">
                                            {user.parent ? (
                                                <p className="user-parent">
                                                    Reports to: {user.parent.name}
                                                    {canEditParent(user) && (
                                                        <button
                                                            className="btn btn-sm btn-secondary"
                                                            style={{ marginLeft: '8px' }}
                                                            onClick={() => {
                                                                setEditingParentId(user.id);
                                                                setNewParentId(user.parentId || '');
                                                            }}
                                                        >
                                                            Change Manager
                                                        </button>
                                                    )}
                                                </p>
                                            ) : (
                                                <p className="user-parent">
                                                    No Manager (Unassigned)
                                                    {canEditParent(user) && (
                                                        <button
                                                            className="btn btn-sm btn-secondary"
                                                            style={{ marginLeft: '8px' }}
                                                            onClick={() => {
                                                                setEditingParentId(user.id);
                                                                setNewParentId('');
                                                            }}
                                                        >
                                                            Assign Manager
                                                        </button>
                                                    )}
                                                </p>
                                            )}
                                        </div>
                                    )}
                                    {user.children && user.children.length > 0 && (
                                        <p className="user-children">
                                            Manages: {user.children.map(child => child.name).join(', ')}
                                        </p>
                                    )}
                                    {canEditUser(user) && (
                                        <div className="user-browser-access">
                                            <div className="browser-access-header">
                                                <div className="browser-access-label">
                                                    <span className="browser-access-title">Browser Access</span>
                                                    <span className={`browser-access-status ${user.browser ? 'enabled' : 'disabled'}`}>
                                                        {user.browser ? 'Enabled' : 'Disabled'}
                                                    </span>
                                                </div>
                                                <label className="browser-access-toggle">
                                                    <input
                                                        type="checkbox"
                                                        checked={user.browser || false}
                                                        onChange={(e) => handleUpdateBrowser(user.id, e.target.checked)}
                                                        className="browser-access-checkbox"
                                                    />
                                                    <span className="browser-access-toggle-label">
                                                        {user.browser ? 'Disable' : 'Enable'}
                                                    </span>
                                                </label>
                                            </div>
                                            <p className="browser-access-description">
                                                {user.browser 
                                                    ? 'User can access the application via web browser'
                                                    : 'User must use the Desktop App to access the application'}
                                            </p>
                                        </div>
                                    )}
                                    <p className="user-dates">
                                        Created: {new Date(user.createdAt || '').toLocaleDateString()}
                                    </p>
                                    {assignedContentSources[user.id] && assignedContentSources[user.id].length > 0 && (
                                        <div className="user-assigned-content-sources styled-box">
                                            <strong>Assigned Content Sources:</strong>
                                            <div className="assigned-content-sources-list">
                                                {assignedContentSources[user.id].map(contentSource => (
                                                    <span key={contentSource.id} className="assigned-content-source-pill">
                                                        {contentSource.title}
                                                        <span className={`content-source-status-badge status-${contentSource.status.replace(/ /g, '-')}`}>{contentSource.status}</span>
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <div className="user-actions">
                                    {canDeleteUser(user) && (
                                        <button
                                            className="btn btn-sm btn-danger"
                                            onClick={() => handleDeleteUser(user.id, user.name)}
                                        >
                                            Delete
                                        </button>
                                    )}
                                </div>
                            </div>
                        );

                        return (
                            <div className="users-columns">
                                {availableRoles.map(role => (
                                    <div key={role} className="role-column">
                                        <div className={`role-column-header ${role}`}>
                                            <h2 className={`role-title ${role}`}>
                                                {role.charAt(0).toUpperCase() + role.slice(1)}s
                                                <span className="user-count">({usersByRole[role].length})</span>
                                            </h2>
                                        </div>
                                        <div className="role-column-content">
                                            {usersByRole[role].map(renderUserCard)}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        );
                    })()
                )}
            </div>

            <ConfirmDialog
                isOpen={dialogState.isOpen}
                onConfirm={dialogState.onConfirm}
                onCancel={dialogState.onCancel}
                title={dialogState.title}
                message={dialogState.message}
                confirmText={dialogState.confirmText}
                cancelText={dialogState.cancelText}
                isDestructive={dialogState.isDestructive}
            />
        </div>
    );
};

export default UserManagement; 